import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authSessionExchange, authMe, setToken } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [status, setStatus] = useState('Authenticating...');
  const [error, setError] = useState(null);
  // CRITICAL: useRef flag prevents double-execution from React Strict Mode.
  // Unlike useState, refs don't reset between Strict Mode's mount/unmount/remount cycles.
  const hasRun = useRef(false);

  useEffect(() => {
    // If this effect already ran (React Strict Mode double-invoke), skip immediately.
    if (hasRun.current) return;
    hasRun.current = true;

    const exchange = async () => {
      try {
        const hash = window.location.hash;
        const search = window.location.search;
        const params = new URLSearchParams(search);

        // Native Google OAuth flow: backend already created the session and
        // redirected here with ?native_token=SESSION_TOKEN
        const nativeToken = params.get('native_token');
        if (nativeToken) {
          process.env.NODE_ENV !== 'production' && console.log('[AuthCallback] Native Google OAuth — token received, loading user...');
          setStatus('Verifying your account...');
          setToken(nativeToken);
          // Small delay to ensure token is persisted before API call
          await new Promise(r => setTimeout(r, 200));
          // Load user profile using the session token (retry once on failure)
          let meRes;
          try {
            meRes = await authMe();
          } catch (firstErr) {
            console.warn('[AuthCallback] First authMe attempt failed, retrying...', firstErr.message);
            await new Promise(r => setTimeout(r, 1000));
            setToken(nativeToken); // re-set in case interceptor cleared it
            meRes = await authMe();
          }
          const userData = meRes.data;
          if (!userData || !userData.user_id) {
            console.error('[AuthCallback] /auth/me returned no user data');
            setError('Authentication failed. Please try again.');
            setTimeout(() => navigate('/login', { replace: true }), 2000);
            return;
          }
          process.env.NODE_ENV !== 'production' && console.log('[AuthCallback] Native OAuth success. User role:', userData.role);
          setUser({ ...userData, session_token: nativeToken });
          setStatus('Success! Loading...');
          const isPlatform = ['platform_owner', 'platform_admin', 'support_admin'].includes(userData.role);
          const needsOnboarding = !userData.active_workspace_id && userData.role === 'pending';
          const target = isPlatform ? '/internal/control-room' : needsOnboarding ? '/onboarding' : '/dashboard';
          window.history.replaceState(null, '', target);
          setTimeout(() => navigate(target, { replace: true }), 100);
          return;
        }

        // Emergent OAuth flow: Extract session_id from query params or hash
        let sessionId = params.get('session_id');
        if (!sessionId && hash.includes('session_id=')) {
          sessionId = hash.split('session_id=')[1]?.split('&')[0];
        }

        if (!sessionId) {
          console.error('[AuthCallback] No session_id found in URL. hash:', hash, 'search:', search);
          setError('No session ID found. Please try logging in again.');
          setTimeout(() => navigate('/login', { replace: true }), 2000);
          return;
        }

        process.env.NODE_ENV !== 'production' && console.log('[AuthCallback] Exchanging session_id (first and only attempt)...');
        setStatus('Verifying your account...');

        // Use GET endpoint — immune to proxy body-stripping issues
        const res = await authSessionExchange(sessionId);
        const data = res.data;

        if (!data || !data.session_token) {
          console.error('[AuthCallback] Exchange succeeded but no session_token in response:', data);
          setError('Authentication failed. No token received.');
          setTimeout(() => navigate('/login', { replace: true }), 2000);
          return;
        }

        process.env.NODE_ENV !== 'production' && console.log('[AuthCallback] Success. User role:', data.role);
        setToken(data.session_token);
        setUser(data);
        setStatus('Success! Loading...');

        const isPlatform = ['platform_owner', 'platform_admin', 'support_admin'].includes(data.role);
        const needsOnboarding = !data.active_workspace_id && data.role === 'pending';
        const target = isPlatform ? '/internal/control-room' : needsOnboarding ? '/onboarding' : '/dashboard';

        window.history.replaceState(null, '', target);
        setTimeout(() => navigate(target, { replace: true }), 100);

      } catch (err) {
        const status = err?.response?.status;
        const msg = err?.response?.data?.detail || err?.message || 'Unknown error';
        console.error(`[AuthCallback] Exchange failed. Status: ${status}, Message: ${msg}`, err);
        setError(`Login failed: ${msg}. Please try again.`);
        setTimeout(() => navigate('/login', { replace: true }), 5000);
      }
    };

    exchange();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4 p-6">
        {!error ? (
          <>
            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-sm text-muted-foreground">{status}</p>
          </>
        ) : (
          <>
            <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto">
              <span className="text-red-600 dark:text-red-400 text-lg font-bold">!</span>
            </div>
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            <button 
              onClick={() => navigate('/login', { replace: true })}
              className="text-xs text-primary underline mt-2"
            >
              Back to login
            </button>
          </>
        )}
      </div>
    </div>
  );
}
